import { validateCPF, validateCNPJ, validateEmail } from '@/lib/utils/validation'

/**
 * Who is paying, as the payment provider needs it.
 *
 * Pagar.me refuses an order without the buyer's document ("The customer
 * Document is required." — BUGS.md FIN-29), and the API sends the order
 * anyway, so the checkout has to collect and check it before submitting.
 */
export interface CustomerData {
  name: string
  email: string
  /** Digits only: 11 for a CPF, 14 for a CNPJ. */
  document: string
  /** Brazilian national number, digits only: area code + number. */
  phone: string
}

export type CustomerField = keyof CustomerData

export const emptyCustomerData = (): CustomerData => ({ name: '', email: '', document: '', phone: '' })

export const onlyDigits = (value: string): string => value.replace(/\D/g, '')

export const documentTypeOf = (document: string): 'CPF' | 'CNPJ' =>
  onlyDigits(document).length > 11 ? 'CNPJ' : 'CPF'

/**
 * `+5511988887777` → `11988887777`.
 *
 * The API builds the provider's phone with a fixed `55` country code and the
 * first two digits as the area code, so only a Brazilian number can be sent.
 * A number from another country is not prefilled: the buyer types one.
 */
export const nationalBrazilianPhone = (e164?: string | null): string => {
  const digits = onlyDigits(e164 || '')
  return digits.startsWith('55') && (digits.length === 12 || digits.length === 13) ? digits.slice(2) : ''
}

/** The CPF/CNPJ on the account (`GET /auth/me`), digits only; '' when none. */
export const accountDocument = (me?: { document?: unknown } | null): string =>
  typeof me?.document === 'string' ? onlyDigits(me.document) : ''

/**
 * The i18n key (in the `checkout` namespace) of the first problem with each
 * field. An empty object means the data can be sent.
 */
export const validateCustomerData = (data: CustomerData): Partial<Record<CustomerField, string>> => {
  const errors: Partial<Record<CustomerField, string>> = {}

  if (data.name.trim().length < 3) errors.name = 'purchase.errors.nameRequired'

  if (!data.email.trim()) errors.email = 'purchase.errors.emailRequired'
  else if (!validateEmail(data.email.trim())) errors.email = 'purchase.errors.emailInvalid'

  const document = onlyDigits(data.document)
  if (!document) errors.document = 'purchase.errors.documentRequired'
  else if (document.length === 11 ? !validateCPF(document) : document.length === 14 ? !validateCNPJ(document) : true) {
    errors.document = document.length === 14 ? 'purchase.errors.cnpjInvalid' : 'purchase.errors.cpfInvalid'
  }

  const phone = onlyDigits(data.phone)
  if (!phone) errors.phone = 'purchase.errors.phoneRequired'
  else if (phone.length < 10 || phone.length > 11) errors.phone = 'purchase.errors.phoneInvalid'

  return errors
}

/** The `customerData` body of `POST /api/checkout`. */
export const toCustomerPayload = (data: CustomerData) => ({
  name: data.name.trim(),
  email: data.email.trim(),
  document: onlyDigits(data.document),
  documentType: documentTypeOf(data.document),
  phone: onlyDigits(data.phone)
})
